// Subir varios productos desde un .zip o una carpeta soltada
// (ADR-2026-10-16-subir-varios-con-zip-y-carpetas): cada carpeta con fotos es UN
// producto, que se llama como esa carpeta, y sus fotos son las del producto. Las
// fotos sueltas en la raíz siguen el camino de siempre (cada una, un producto).
//
// Acá vive SOLO la regla, sobre rutas: no sabe de fflate ni del DOM. La usan el
// lector del .zip y el de carpetas soltadas (`leerLoteDeProductos.ts`), así que
// las dos puertas arman los productos igual.

import { MAX_IMAGENES_POR_PRODUCTO } from "./limiteImagenesProducto";

/** Las que `normalizeImageForUpload` acepta (HEIC/HEIF/AVIF/WEBP se convierten
 *  a JPG). Lo demás que venga en el lote se saltea y se cuenta. */
export const EXTENSIONES_DE_IMAGEN = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "heic",
  "heif",
  "avif",
] as const;

export interface EntradaDeLote<T> {
  /** Ruta dentro del lote, con `/` (o `\` de un .zip armado en Windows). */
  ruta: string;
  dato: T;
}

export interface CarpetaDeProducto<T> {
  /** Ruta de la carpeta dentro del lote, ya sin el envoltorio. Única. */
  ruta: string;
  /** Nombre del producto: el de la carpeta que contiene las fotos. */
  nombre: string;
  /** Fotos en orden natural por nombre; la primera es la portada. */
  archivos: T[];
  /** Cuántas fotos quedaron afuera por el tope por producto. */
  recortadas: number;
}

export interface LotePorCarpetas<T> {
  carpetas: CarpetaDeProducto<T>[];
  /** Fotos en la raíz del lote (sin carpeta). */
  sueltas: T[];
  /** Archivos que no son fotos compatibles (la basura del sistema no cuenta). */
  ignorados: number;
}

// NFC: Mac puede guardar "ó" como "o" + tilde suelta; sin esto dos carpetas que se
// ven iguales serían dos productos y el nombre llegaría descompuesto.
const normalizarRuta = (ruta: string): string =>
  ruta
    .normalize("NFC")
    .replace(/\\/g, "/")
    .replace(/\/{2,}/g, "/")
    .replace(/^(\.\/|\/)+/, "");

const extension = (ruta: string): string => {
  const base = ruta.slice(ruta.lastIndexOf("/") + 1);
  const punto = base.lastIndexOf(".");
  return punto <= 0 ? "" : base.slice(punto + 1).toLowerCase();
};

export const esRutaDeImagen = (ruta: string): boolean =>
  (EXTENSIONES_DE_IMAGEN as readonly string[]).includes(extension(ruta));

/** Lo que agregan el sistema y los compresores: no es del usuario y ni siquiera
 *  se cuenta como salteado. `__MACOSX/` y `._foto.jpg` son del "Comprimir" de Mac,
 *  y tienen extensión de imagen: sin esto se colarían como fotos rotas. */
const esBasura = (ruta: string): boolean => {
  if (ruta.endsWith("/")) return true; // entrada de directorio del .zip
  const partes = ruta.split("/");
  if (partes.some((p) => p === "__MACOSX" || p.startsWith("."))) return true;
  const base = partes[partes.length - 1].toLowerCase();
  return base === "thumbs.db" || base === "desktop.ini";
};

/** Orden natural: `foto 2` antes que `foto 10`, sin importar mayúsculas. */
export const compararNatural = (a: string, b: string): number =>
  a.localeCompare(b, "es", { numeric: true, sensitivity: "base" });

/**
 * Arma los productos de un lote a partir de las rutas.
 *
 * - Cada carpeta con fotos adentro es un producto, con el nombre de esa carpeta
 *   (la más cercana a las fotos): `Remeras/Negra/1.jpg` es el producto "Negra".
 * - Envoltorio: el "Comprimir" de Mac (y soltar una carpeta) mete todo en una
 *   carpeta raíz. Si todo cuelga de la misma carpeta y adentro HAY subcarpetas, se
 *   saltea: si no, las fotos sueltas de adentro serían un único producto llamado
 *   como el .zip. Si adentro sólo hay fotos, es UN producto (alguien comprimió la
 *   carpeta de un producto).
 * - Más de `MAX_IMAGENES_POR_PRODUCTO` fotos: se toman las primeras y se cuenta el
 *   resto en `recortadas`, para avisar.
 */
export function organizarPorCarpetas<T>(
  entradas: EntradaDeLote<T>[],
): LotePorCarpetas<T> {
  let ignorados = 0;
  let validas: { partes: string[]; dato: T }[] = [];
  for (const e of entradas) {
    const ruta = normalizarRuta(e.ruta);
    if (!ruta || esBasura(ruta)) continue;
    if (!esRutaDeImagen(ruta)) {
      ignorados += 1;
      continue;
    }
    validas.push({ partes: ruta.split("/"), dato: e.dato });
  }

  while (
    validas.length > 0 &&
    validas.every(
      (v) => v.partes.length >= 2 && v.partes[0] === validas[0].partes[0],
    ) &&
    validas.some((v) => v.partes.length >= 3)
  ) {
    validas = validas.map((v) => ({ ...v, partes: v.partes.slice(1) }));
  }

  const porCarpeta = new Map<string, { nombre: string; partes: string[]; dato: T }[]>();
  const sueltas: { nombre: string; dato: T }[] = [];
  for (const v of validas) {
    const nombre = v.partes[v.partes.length - 1];
    const carpeta = v.partes.slice(0, -1).join("/");
    if (!carpeta) {
      sueltas.push({ nombre, dato: v.dato });
      continue;
    }
    const lista = porCarpeta.get(carpeta) ?? [];
    lista.push({ nombre, partes: v.partes, dato: v.dato });
    porCarpeta.set(carpeta, lista);
  }

  const carpetas = [...porCarpeta.entries()]
    .sort(([a], [b]) => compararNatural(a, b))
    .map(([ruta, fotos]) => {
      const ordenadas = [...fotos].sort((a, b) => compararNatural(a.nombre, b.nombre));
      const partes = fotos[0].partes;
      const nombre = partes[partes.length - 2].trim() || ruta;
      return {
        ruta,
        nombre,
        archivos: ordenadas.slice(0, MAX_IMAGENES_POR_PRODUCTO).map((f) => f.dato),
        recortadas: Math.max(0, ordenadas.length - MAX_IMAGENES_POR_PRODUCTO),
      };
    });

  return {
    carpetas,
    sueltas: sueltas
      .sort((a, b) => compararNatural(a.nombre, b.nombre))
      .map((s) => s.dato),
    ignorados,
  };
}

/**
 * Deja entrar hasta `max` productos ANTES de descomprimir o normalizar, para no gastar en
 * fotos que el límite de la carga va a rechazar igual. Cada carpeta cuenta como uno. Las
 * sueltas no se pueden contar acá: con "Nombres de archivo" prendido, `buzo_1…5` son UN
 * producto, y esa regla vive en el hook (review Greptile #184). Se las corta por fotos, en
 * lo máximo que podría llegar a entrar (`max` productos de 5 fotos); el corte exacto, por
 * productos y con el modo de la pantalla, lo hace después `entrarEnElLimite`.
 */
export function limitarProductos<T>(
  lote: LotePorCarpetas<T>,
  max: number,
): { lote: LotePorCarpetas<T>; omitidos: number } {
  const cupo = Math.max(0, max);
  const carpetas = lote.carpetas.slice(0, cupo);
  const sueltas = lote.sueltas.slice(0, cupo * MAX_IMAGENES_POR_PRODUCTO);
  return {
    lote: { ...lote, carpetas, sueltas },
    omitidos:
      lote.carpetas.length + lote.sueltas.length - carpetas.length - sueltas.length,
  };
}

/** Cambia el dato de cada foto sin tocar la estructura. Si `fn` devuelve null la
 *  foto se cae (y una carpeta que se queda sin fotos, también). */
export function mapearLote<T, U>(
  lote: LotePorCarpetas<T>,
  fn: (dato: T) => U | null,
): LotePorCarpetas<U> {
  const vivos = (xs: T[]) => xs.map(fn).filter((x): x is U => x !== null);
  return {
    carpetas: lote.carpetas
      .map((c) => ({ ...c, archivos: vivos(c.archivos) }))
      .filter((c) => c.archivos.length > 0),
    sueltas: vivos(lote.sueltas),
    ignorados: lote.ignorados,
  };
}

/**
 * Repara el nombre de una entrada de .zip. fflate lee como latin1 los nombres que
 * no traen la marca de UTF-8, y el "Comprimir" de Mac no la pone aunque escribe en
 * UTF-8: "Camisón" llegaba como "CamisÃ³n". Si los bytes forman UTF-8 válido, era
 * eso; si no (un .zip de Windows en otra codificación), queda como vino.
 */
export function repararNombreDeZip(nombre: string): string {
  // Algún caracter fuera de latin1: fflate ya lo decodificó como UTF-8.
  if (/[^\u0000-ÿ]/.test(nombre)) return nombre;
  if (!/[\u0080-ÿ]/.test(nombre)) return nombre; // ASCII puro
  const bytes = Uint8Array.from(nombre, (c) => c.charCodeAt(0));
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return nombre;
  }
}

/** Arma los textos para el usuario de lo que el lote dejó afuera sin ser error. */
export function avisosDelLote(lote: LotePorCarpetas<unknown>): string[] {
  const avisos: string[] = [];
  const recortadas = lote.carpetas.filter((c) => c.recortadas > 0);
  if (recortadas.length === 1) {
    const c = recortadas[0];
    avisos.push(
      `La carpeta "${c.nombre}" tenía ${c.archivos.length + c.recortadas} fotos: tomamos las primeras ${MAX_IMAGENES_POR_PRODUCTO}.`,
    );
  } else if (recortadas.length > 1) {
    avisos.push(
      `${recortadas.length} carpetas tenían más de ${MAX_IMAGENES_POR_PRODUCTO} fotos: tomamos las primeras ${MAX_IMAGENES_POR_PRODUCTO} de cada una.`,
    );
  }
  if (lote.ignorados > 0) {
    avisos.push(
      `Salteamos ${lote.ignorados === 1 ? "1 archivo que no es" : `${lote.ignorados} archivos que no son`} foto (JPG, PNG, WEBP, HEIC o AVIF).`,
    );
  }
  return avisos;
}
